import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { auth } from "../firebase";
import { signInWithEmailAndPassword } from "firebase/auth";
import { useToast } from "../context/ToastContext";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { success, error } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await signInWithEmailAndPassword(auth, email, password);
      success("Welcome back! Login successful.");
      navigate("/");
    } catch (err) {
      error("Invalid email or password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        {/* Left Side - Visual */}
        <div className="auth-visual">
          <div className="auth-visual-content">
            <div className="auth-brand">
              <img
                src="/logo.png"
                alt="Kumbhar Samaj Logo"
                className="brand-logo"
              />
              <span className="brand-text">Kumbhar Samaj Onnati Mandal</span>
            </div>
            <h2>Welcome Back</h2>
            <p>
              Continue your journey to find the perfect match within our trusted
              community.
            </p>
            <div className="auth-features">
              <div className="feature-item">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                <span>Verified Profiles</span>
              </div>
              <div className="feature-item">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                <span>100% Secure & Private</span>
              </div>
              <div className="feature-item">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                <span>Growing Community</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="auth-form-wrapper">
          <div className="auth-form">
            <div className="form-header">
              <h1>Sign In</h1>
              <p>Enter your credentials to access your account</p>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Email Address</label>
                <input
                  type="email"
                  className="form-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Password</label>
                <input
                  type="password"
                  className="form-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  autoComplete="current-password"
                />
              </div>

              <div className="form-footer">
                <label className="checkbox-label">
                  <input type="checkbox" />
                  <span>Remember me</span>
                </label>
                <Link to="/forgot-password" className="link-primary">
                  Forgot password?
                </Link>
              </div>

              <button
                type="submit"
                className="btn btn-primary btn-lg w-full"
                disabled={loading}
              >
                {loading ? "Signing in..." : "Sign In"}
              </button>
            </form>

            <div className="form-divider">
              <span>New to Kumbhar Samaj Onnati Mandal?</span>
            </div>

            <Link to="/register" className="btn btn-outline btn-lg w-full">
              Create Account
            </Link>
          </div>
        </div>
      </div>

      <style jsx>{`
        .auth-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: var(--space-4);
          background: var(--neutral-50);
        }

        .auth-container {
          display: grid;
          grid-template-columns: 1fr 1fr;
          max-width: 1200px;
          width: 100%;
          background: white;
          border-radius: var(--radius-2xl);
          overflow: hidden;
          box-shadow: var(--shadow-xl);
        }

        .auth-visual {
          background: linear-gradient(
            135deg,
            var(--primary-700) 0%,
            var(--primary-500) 100%
          );
          padding: var(--space-16);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .auth-visual-content {
          max-width: 480px;
        }

        .auth-brand {
          display: flex;
          align-items: center;
          gap: var(--space-2);
          font-family: var(--font-display);
          font-size: var(--text-2xl);
          font-weight: var(--weight-bold);
          margin-bottom: var(--space-8);
        }

        .brand-icon {
          font-size: var(--text-4xl);
        }

        .brand-logo {
          width: 50px;
          height: 50px;
          object-fit: cover;
          border-radius: 50%;
          border: 2px solid #d6af36;
          background: white;
        }

        .auth-visual h2 {
          font-size: var(--text-4xl);
          color: white;
          margin-bottom: var(--space-4);
          line-height: var(--leading-tight);
        }

        .auth-visual p {
          font-size: var(--text-lg);
          color: rgba(255, 255, 255, 0.9);
          line-height: var(--leading-relaxed);
          margin-bottom: var(--space-12);
        }

        .auth-features {
          display: flex;
          flex-direction: column;
          gap: var(--space-4);
        }

        .feature-item {
          display: flex;
          align-items: center;
          gap: var(--space-3);
          font-size: var(--text-base);
          color: rgba(255, 255, 255, 0.95);
        }

        .auth-form-wrapper {
          padding: var(--space-16);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .auth-form {
          width: 100%;
          max-width: 420px;
        }

        .form-header {
          text-align: center;
          margin-bottom: var(--space-8);
        }

        .form-header h1 {
          font-size: var(--text-4xl);
          margin-bottom: var(--space-2);
        }

        .form-header p {
          color: var(--neutral-600);
          margin: 0;
        }

        .form-group {
          margin-bottom: var(--space-6);
        }

        .form-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: var(--space-6);
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: var(--space-2);
          font-size: var(--text-sm);
          color: var(--neutral-700);
          cursor: pointer;
        }

        .link-primary {
          font-size: var(--text-sm);
          color: var(--primary-500);
          text-decoration: none;
          font-weight: var(--weight-medium);
        }

        .link-primary:hover {
          color: var(--primary-700);
        }

        .w-full {
          width: 100%;
        }

        .form-divider {
          text-align: center;
          margin: var(--space-8) 0;
          position: relative;
        }

        .form-divider::before,
        .form-divider::after {
          content: "";
          position: absolute;
          top: 50%;
          width: 40%;
          height: 1px;
          background: var(--neutral-200);
        }

        .form-divider::before {
          left: 0;
        }

        .form-divider::after {
          right: 0;
        }

        .form-divider span {
          background: white;
          padding: 0 var(--space-4);
          font-size: var(--text-sm);
          color: var(--neutral-600);
        }

        @media (max-width: 768px) {
          .auth-container {
            grid-template-columns: 1fr;
          }

          .auth-visual {
            display: none;
          }

          .auth-form-wrapper {
            padding: var(--space-8);
          }
        }
      `}</style>
    </div>
  );
};

export default LoginPage;
